import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { 
  Table, Button, Space, Card, Modal, Form, Input, InputNumber, Select, 
  Tag, Popconfirm, message, Row, Col, Divider, Tooltip, Typography, Tabs 
} from 'antd';
import { 
  PlusOutlined, EditOutlined, DeleteOutlined, MailOutlined, PhoneOutlined,
  SearchOutlined, ReloadOutlined, UserOutlined, BankOutlined, DollarOutlined,
  CalendarOutlined, DownloadOutlined
} from '@ant-design/icons';
import {
  fetchStaff, createStaff, updateStaff, deleteStaff
} from '../../../../Service/Staff_API/staffApi';
import { fetchAcademicYears } from '../../../../Service/Syllabus_API/syllabusApi';

const { Option } = Select;
const { Text } = Typography;

const formatDate = (dateStr) => {
  if (!dateStr || dateStr === '0000-00-00') return 'N/A';
  const parts = dateStr.split('-');
  if (parts.length === 3) {
    return `${parts[2]}-${parts[1]}-${parts[0]}`;
  }
  return dateStr;
};

const formatCurrency = (val) => {
  const num = parseFloat(val);
  if (isNaN(num)) return '₹0.00';
  return `₹${num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const getDefaultAcademicYearId = (ayList) => {
  if (!ayList || ayList.length === 0) return 'all';
  const activeAy = ayList.find(ay => ay.status === 'Active' || ay.status === 'ACTIVE');
  return activeAy ? String(activeAy.academic_year_id) : String(ayList[0].academic_year_id);
};

const StaffManagementPage = () => {
  const [data, setData] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [form] = Form.useForm();

  // Search & Filter States
  const [searchText, setSearchText] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [academicYearFilter, setAcademicYearFilter] = useState('all');

  // Form watchers for dynamic Net Salary calculation
  const watchBaseSalary = Form.useWatch('base_salary', form) || 0;
  const watchTdsPercent = Form.useWatch('tds_percent', form) || 0;

  const calculatedNetSalary = useMemo(() => {
    const base = parseFloat(watchBaseSalary) || 0;
    const tds = parseFloat(watchTdsPercent) || 0;
    const deduction = base * (tds / 100);
    return Math.max(0, base - deduction);
  }, [watchBaseSalary, watchTdsPercent]);

  // Fetch Academic Years on Mount and default to Active Academic Year
  useEffect(() => {
    fetchAcademicYears()
      .then(res => {
        const ayList = res || [];
        setAcademicYears(ayList);
        const activeAyId = getDefaultAcademicYearId(ayList);
        if (activeAyId !== 'all') {
          setAcademicYearFilter(activeAyId);
        }
      })
      .catch(() => setAcademicYears([]));
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (searchText && searchText.trim()) params.search = searchText.trim();
      if (typeFilter && typeFilter !== 'all') params.staff_type = typeFilter;
      if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
      if (academicYearFilter && academicYearFilter !== 'all') params.academic_year_id = academicYearFilter;
      
      const result = await fetchStaff(params);
      setData(result || []);
    } catch (e) {
      message.error('Failed to load staff list.');
    } finally {
      setLoading(false);
    }
  }, [searchText, typeFilter, statusFilter, academicYearFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Dynamic Staff Types lookup
  const availableTypes = useMemo(() => {
    const types = new Set(['IT-SUPPORT', 'COORDINATOR']);
    (data || []).forEach(item => {
      if (item && item.staff_type) types.add(item.staff_type.toUpperCase());
    });
    return Array.from(types);
  }, [data]);

  const filteredData = useMemo(() => {
    return data || [];
  }, [data]);

  const handleClearFilters = () => {
    setSearchText('');
    setTypeFilter('all');
    setStatusFilter('all');
    setAcademicYearFilter(getDefaultAcademicYearId(academicYears));
  };

  const handleSave = async (values) => {
    try {
      const payload = {
        ...values,
        base_salary: parseFloat(values.base_salary) || 0,
        tds_percent: parseFloat(values.tds_percent) || 0,
        academic_year_id: values.academic_year_id ? parseInt(values.academic_year_id) : null
      };

      if (editingItem) {
        await updateStaff(editingItem.staff_id, payload);
        message.success('Staff member updated successfully.');
      } else {
        await createStaff(payload);
        message.success('Staff member created successfully.');
      }
      setIsModalOpen(false);
      form.resetFields();
      setEditingItem(null);
      loadData();
    } catch (e) {
      message.error(e.message || 'Failed to save staff member.');
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteStaff(id);
      message.success('Staff member deleted successfully.');
      loadData();
    } catch (e) {
      message.error(e.message || 'Failed to delete staff member.');
    }
  };

  const handleExportCSV = () => {
    if (!filteredData || filteredData.length === 0) {
      message.warning('No staff data available to export.');
      return;
    }
    const headers = [
      'Staff Code', 'Staff Name', 'Email', 'Contact', 'Staff Type', 'Status', 
      'Academic Year', 'Base Salary (INR)', 'TDS Deduction (%)', 'Net Salary (INR)', 
      'Bank Name', 'Account Holder Name', 'Account Number', 'IFSC Code', 'Branch Name', 'PAN Number', 
      'Join Date', 'Exit Date'
    ];
    
    const csvRows = filteredData.map(item => {
      const base = parseFloat(item.base_salary) || 0;
      const tds = parseFloat(item.tds_percent) || 0;
      const net = base - (base * (tds / 100));
      return [
        `"${(item.staff_code || '').replace(/"/g, '""')}"`,
        `"${(item.staff_name || '').replace(/"/g, '""')}"`,
        `"${(item.staff_email || '').replace(/"/g, '""')}"`,
        `"${(item.staff_contact || '').replace(/"/g, '""')}"`,
        `"${(item.staff_type || '').replace(/"/g, '""')}"`,
        `"${(item.status || 'Active').replace(/"/g, '""')}"`,
        `"${(item.academic_year || '').replace(/"/g, '""')}"`,
        base.toFixed(2),
        tds.toFixed(2),
        net.toFixed(2),
        `"${(item.bank_name || '').replace(/"/g, '""')}"`,
        `"${(item.bank_account_name || '').replace(/"/g, '""')}"`,
        `"${(item.account_number || '').replace(/"/g, '""')}"`,
        `"${(item.ifsc_code || '').replace(/"/g, '""')}"`,
        `"${(item.branch_name || '').replace(/"/g, '""')}"`,
        `"${(item.pan_number || '').replace(/"/g, '""')}"`,
        formatDate(item.join_date),
        formatDate(item.exit_date)
      ].join(',');
    });

    const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...csvRows].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `Staff_Directory_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    message.success('Staff directory exported to CSV.');
  };

  const columns = [
    { 
      title: 'Name & Code', 
      key: 'name_code', 
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--text-color)' }}>{record.staff_name}</div>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--primary-color, #1890ff)', marginTop: 2 }}>
            {record.staff_code || 'N/A'}
          </div>
          {record.academic_year && (
            <Tag color="cyan" style={{ marginTop: 4, fontSize: 11, fontWeight: 500 }}>
              <CalendarOutlined style={{ marginRight: 4 }} />
              {record.academic_year}
            </Tag>
          )}
        </div>
      )
    },
    {
      title: 'Email / Contact',
      key: 'email_contact',
      render: (_, record) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <MailOutlined style={{ fontSize: 12, color: '#3b82f6' }} />
            <span>{record.staff_email || 'N/A'}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-secondary)' }}>
            <PhoneOutlined style={{ fontSize: 11, color: '#10b981' }} />
            <span>{record.staff_contact || 'N/A'}</span>
          </div>
        </div>
      )
    },
    {
      title: 'Staff Type',
      dataIndex: 'staff_type',
      key: 'staff_type',
      render: (type) => <Tag color={type === 'IT-SUPPORT' ? 'purple' : 'blue'} style={{ fontWeight: 600 }}>{type || 'N/A'}</Tag>
    },
    {
      title: 'Salary & TDS',
      key: 'salary_details',
      render: (_, record) => {
        const base = parseFloat(record.base_salary) || 0;
        const tds = parseFloat(record.tds_percent) || 0;
        const net = base - (base * (tds / 100));
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#10b981' }}>
              Base: {formatCurrency(base)}
            </div>
            {tds > 0 ? (
              <div style={{ fontSize: 11, color: '#ef4444' }}>
                TDS: {tds}% (-{formatCurrency(base * (tds / 100))})
              </div>
            ) : (
              <div style={{ fontSize: 11, color: '#888' }}>TDS: 0%</div>
            )}
            <div style={{ fontSize: 12, fontWeight: 700, color: '#3b82f6' }}>
              Net: {formatCurrency(net)}
            </div>
          </div>
        );
      }
    },
    {
      title: 'Bank Details',
      key: 'bank_details',
      render: (_, record) => {
        if (!record.bank_name && !record.account_number && !record.pan_number) {
          return <Text type="secondary" style={{ fontSize: 12 }}>Not Provided</Text>;
        }
        return (
          <Tooltip title={
            <div>
              <div><strong>Account Name:</strong> {record.bank_account_name || 'N/A'}</div>
              <div><strong>Bank:</strong> {record.bank_name || 'N/A'}</div>
              <div><strong>A/C No:</strong> {record.account_number || 'N/A'}</div>
              <div><strong>IFSC:</strong> {record.ifsc_code || 'N/A'}</div>
              <div><strong>Branch:</strong> {record.branch_name || 'N/A'}</div>
              <div><strong>PAN No:</strong> {record.pan_number || 'N/A'}</div>
            </div>
          }>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2, cursor: 'pointer' }}>
              <div style={{ fontWeight: 600, fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
                <BankOutlined style={{ color: '#8b5cf6' }} />
                <span>{record.bank_name || 'Bank'}</span>
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                A/C: {record.account_number ? `****${record.account_number.slice(-4)}` : 'N/A'}
              </div>
              <div style={{ fontSize: 11, color: '#6b7280' }}>
                IFSC: {record.ifsc_code || 'N/A'}
              </div>
              {record.pan_number && (
                <div style={{ fontSize: 11, color: '#8b5cf6', fontWeight: 600 }}>
                  PAN: {record.pan_number}
                </div>
              )}
            </div>
          </Tooltip>
        );
      }
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => <Tag color={status === 'Active' ? 'green' : 'red'} style={{ fontWeight: 700 }}>{(status || 'Active').toUpperCase()}</Tag>
    },
    { 
      title: 'Join / Exit Date', 
      key: 'join_exit_dates', 
      render: (_, record) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: 12 }}>
          <div><span style={{ color: '#10b981', fontWeight: 600 }}>Join:</span> {formatDate(record.join_date)}</div>
          <div><span style={{ color: record.exit_date && record.exit_date !== '0000-00-00' ? '#ef4444' : '#888', fontWeight: 600 }}>Exit:</span> {formatDate(record.exit_date)}</div>
        </div>
      ) 
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => { 
            setEditingItem(record); 
            form.setFieldsValue({
              ...record,
              academic_year_id: record.academic_year_id ? String(record.academic_year_id) : undefined
            }); 
            setIsModalOpen(true); 
          }} />
          <Popconfirm title="Delete this staff member?" onConfirm={() => handleDelete(record.staff_id)}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      )
    }
  ];

  const hasActiveFilters = searchText || typeFilter !== 'all' || statusFilter !== 'all' || academicYearFilter !== getDefaultAcademicYearId(academicYears);

  return (
    <div style={{ padding: 24, background: 'var(--panel-color)', minHeight: '80vh', color: 'var(--text-color)', border: '1px solid var(--border-color)', borderRadius: 12 }}>
      {/* Header Bar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <Space size="middle">
          <UserOutlined style={{ fontSize: 24, color: '#1890ff' }} />
          <h2 style={{ color: 'var(--text-color)', margin: 0 }}>Staff Management</h2>
        </Space>
        <Space wrap>
          <Button icon={<DownloadOutlined />} onClick={handleExportCSV}>
            Export CSV
          </Button>
          <Button icon={<ReloadOutlined />} onClick={loadData} loading={loading}>
            Refresh
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => { 
            setEditingItem(null); 
            form.resetFields(); 
            form.setFieldsValue({
              status: 'Active',
              academic_year_id: academicYearFilter !== 'all' ? academicYearFilter : getDefaultAcademicYearId(academicYears),
              base_salary: 0,
              tds_percent: 0
            });
            setIsModalOpen(true); 
          }}>
            Add Staff Member
          </Button>
        </Space>
      </div>

      <Card style={{ border: '1px solid var(--border-color)', borderRadius: 12 }} bodyStyle={{ padding: 16 }}>
        {/* Search & Filter Toolbar */}
        <Row justify="space-between" align="middle" style={{ marginBottom: 16, gap: 12 }}>
          <Col xs={24} md={10} lg={8}>
            <Input
              placeholder="Search Code, Name, Email, Bank, PAN..."
              prefix={<SearchOutlined style={{ color: '#1890ff' }} />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
              style={{ width: '100%', borderRadius: 8 }}
            />
          </Col>
          <Col xs={24} md={14} lg={15}>
            <Space wrap style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
              <Select showSearch optionFilterProp="children"
                value={academicYearFilter}
                onChange={setAcademicYearFilter}
                style={{ width: 170 }}
                placeholder="Academic Year"
              >
                <Option value="all">All Academic Years</Option>
                {academicYears.map(ay => (
                  <Option key={ay.academic_year_id} value={String(ay.academic_year_id)}>
                    {ay.academic_year} {ay.status === 'Active' || ay.status === 'ACTIVE' ? '(Active)' : ''}
                  </Option>
                ))}
              </Select>

              <Select showSearch optionFilterProp="children"
                value={typeFilter}
                onChange={setTypeFilter}
                style={{ width: 160 }}
                placeholder="Staff Type"
              >
                <Option value="all">All Staff Types</Option>
                {availableTypes.map(t => (
                  <Option key={t} value={t}>{t}</Option>
                ))}
              </Select>

              <Select showSearch optionFilterProp="children"
                value={statusFilter}
                onChange={setStatusFilter}
                style={{ width: 130 }}
                placeholder="Status"
              >
                <Option value="all">All Statuses</Option>
                <Option value="Active">Active</Option>
                <Option value="Inactive">Inactive</Option>
              </Select>

              {hasActiveFilters && (
                <Button onClick={handleClearFilters}>
                  Clear Filters
                </Button>
              )}

              <Tag color="blue" style={{ padding: '4px 10px', borderRadius: 10, fontSize: 12, fontWeight: 600 }}>
                {filteredData.length} / {data.length} Staff
              </Tag>
            </Space>
          </Col>
        </Row>

        <Table 
          dataSource={filteredData} 
          columns={columns} 
          rowKey="staff_id" 
          loading={loading} 
          scroll={{ x: 'max-content' }}
          pagination={{ pageSize: 10, showSizeChanger: true }}
        />
      </Card>

      {/* Add / Edit Staff Modal */}
      <Modal 
        title={
          <Space>
            <UserOutlined style={{ color: '#1890ff' }} />
            <span>{editingItem ? 'Edit Staff Member' : 'Add Staff Member'}</span>
          </Space>
        } 
        open={isModalOpen} 
        onCancel={() => setIsModalOpen(false)} 
        footer={null} 
        destroyOnClose
        width={720}
      >
        <Form 
          form={form} 
          layout="vertical" 
          onFinish={handleSave} 
          initialValues={{ status: 'Active', base_salary: 0, tds_percent: 0 }}
        >
          <Tabs defaultActiveKey="1" items={[
            {
              key: '1',
              label: (
                <span>
                  <UserOutlined /> Basic Information
                </span>
              ),
              children: (
                <Row gutter={16}>
                  <Col xs={24} sm={12}>
                    <Form.Item name="staff_code" label="Staff Code" rules={[{ required: true, message: 'Please enter staff code!' }]}>
                      <Input placeholder="e.g. MF2014002" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item name="staff_name" label="Staff Name" rules={[{ required: true, message: 'Please enter staff name!' }]}>
                      <Input placeholder="e.g. Ramesh Kumar" />
                    </Form.Item>
                  </Col>

                  <Col xs={24} sm={12}>
                    <Form.Item name="staff_email" label="Email Address" rules={[{ required: true, type: 'email', message: 'Please enter valid email!' }]}>
                      <Input placeholder="e.g. staff@example.com" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item name="staff_contact" label="Phone Number">
                      <Input placeholder="e.g. 9876543210" />
                    </Form.Item>
                  </Col>

                  <Col xs={24} sm={12}>
                    <Form.Item name="staff_type" label="Staff Type" rules={[{ required: true, message: 'Please select staff type!' }]}>
                      <Select showSearch optionFilterProp="children" placeholder="Select staff type">
                        <Option value="IT-SUPPORT">IT-SUPPORT</Option>
                        <Option value="COORDINATOR">COORDINATOR</Option>
                        <Option value="ADMIN-STAFF">ADMIN-STAFF</Option>
                        <Option value="MANAGER">MANAGER</Option>
                        <Option value="OTHER">OTHER</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item name="academic_year_id" label="Academic Year">
                      <Select showSearch optionFilterProp="children" placeholder="Select Academic Year" allowClear>
                        {academicYears.map(ay => (
                          <Option key={ay.academic_year_id} value={String(ay.academic_year_id)}>
                            {ay.academic_year} {ay.status === 'Active' || ay.status === 'ACTIVE' ? '(Active)' : ''}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>

                  <Col xs={24} sm={8}>
                    <Form.Item name="status" label="Status" rules={[{ required: true }]}>
                      <Select showSearch optionFilterProp="children" placeholder="Select status">
                        <Option value="Active">Active</Option>
                        <Option value="Inactive">Inactive</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={8}>
                    <Form.Item name="join_date" label="Join Date">
                      <Input type="date" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={8}>
                    <Form.Item name="exit_date" label="Exit Date">
                      <Input type="date" />
                    </Form.Item>
                  </Col>
                </Row>
              )
            },
            {
              key: '2',
              label: (
                <span>
                  <DollarOutlined /> Financial & Salary
                </span>
              ),
              children: (
                <div>
                  <Row gutter={16}>
                    <Col xs={24} sm={12}>
                      <Form.Item name="base_salary" label="Base Salary (₹)" rules={[{ required: true, message: 'Please enter base salary' }]}>
                        <InputNumber 
                          min={0} 
                          step={500} 
                          style={{ width: '100%' }} 
                          placeholder="e.g. 35000" 
                          formatter={value => `₹ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                          parser={value => value.replace(/₹\s?|(,*)/g, '')}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                      <Form.Item name="tds_percent" label="TDS Deduction (%)" rules={[{ required: true, message: 'Please enter TDS percentage' }]}>
                        <InputNumber 
                          min={0} 
                          max={100} 
                          step={0.5} 
                          style={{ width: '100%' }} 
                          placeholder="e.g. 10" 
                          addonAfter="%"
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Card style={{ background: 'var(--panel-color)', border: '1px solid var(--border-color)', borderRadius: 8, marginTop: 8 }}>
                    <Row justify="space-between" align="middle">
                      <Col>
                        <Text type="secondary">Base Salary:</Text>
                        <div style={{ fontSize: 16, fontWeight: 600, color: '#10b981' }}>
                          {formatCurrency(watchBaseSalary)}
                        </div>
                      </Col>
                      <Col>
                        <Text type="secondary">TDS Deduction ({watchTdsPercent}%):</Text>
                        <div style={{ fontSize: 16, fontWeight: 600, color: '#ef4444' }}>
                          -{formatCurrency((parseFloat(watchBaseSalary) || 0) * ((parseFloat(watchTdsPercent) || 0) / 100))}
                        </div>
                      </Col>
                      <Col>
                        <Text type="secondary">Net Salary Payable:</Text>
                        <div style={{ fontSize: 18, fontWeight: 700, color: '#3b82f6' }}>
                          {formatCurrency(calculatedNetSalary)}
                        </div>
                      </Col>
                    </Row>
                  </Card>
                </div>
              )
            },
            {
              key: '3',
              label: (
                <span>
                  <BankOutlined /> Bank Account Details
                </span>
              ),
              children: (
                <Row gutter={16}>
                  <Col xs={24} sm={12}>
                    <Form.Item name="bank_name" label="Bank Name">
                      <Input placeholder="e.g. State Bank of India" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item name="bank_account_name" label="Account Holder Name">
                      <Input placeholder="Name as per Bank Account" />
                    </Form.Item>
                  </Col>

                  <Col xs={24} sm={12}>
                    <Form.Item name="account_number" label="Account Number">
                      <Input placeholder="e.g. 30123456789" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item name="ifsc_code" label="IFSC Code">
                      <Input placeholder="e.g. SBIN0001234" style={{ textTransform: 'uppercase' }} />
                    </Form.Item>
                  </Col>

                  <Col xs={24} sm={12}>
                    <Form.Item name="branch_name" label="Branch Name">
                      <Input placeholder="e.g. Main Branch, MG Road" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item name="pan_number" label="PAN Number">
                      <Input placeholder="e.g. ABCDE1234F" style={{ textTransform: 'uppercase' }} />
                    </Form.Item>
                  </Col>
                </Row>
              )
            }
          ]} />

          <Divider style={{ margin: '16px 0' }} />

          <Form.Item style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 0 }}>
            <Space>
              <Button onClick={() => setIsModalOpen(false)}>Cancel</Button>
              <Button type="primary" htmlType="submit">Save Staff Member</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default StaffManagementPage;
